<template>
    <TransitionSlide :direction="transitionDirection">
        <div class="registration-content" :key="authenticationStep" :class="classForCurrentState">
            <div class="registration-content__header">
                <InfoText
                    :title="modalTitle"
                    :perex="modalPerex"
                    :showBackLink="authenticationStep == 'LoginPass'"
                    class="registration-content__info-text"
                />
                <RegistrationStepsPanel
                    v-show="authenticationStep == 'RegPersonal' || authenticationStep == 'RegVerPhone' || authenticationStep == 'RegPinLog'"
                    class="registration-content__pannel"
                />
            </div>

            <RegistrationPersonal
                ref="registrationDataComponentRef"
                v-show="authenticationStep == 'RegPersonal'"
                class="registration-content__data"
            />

            <PinInput
                ref="pinInputRef"
                v-show="authenticationStep == 'RegVerPhone' || authenticationStep == 'RegPinLog'"
                class="registration-content__pin"
                v-model="authenticationData.pin"
                name="sms_code"
                :phone-number="authenticationData.userName"
                v-model:error-message="authenticationErrors.pinErrorMessage"
                @resend-pin="resendPin"
                @input-error-detected="dataLayerRegistrationError"
            />

            <NewPasswordInput
                ref="newPasswordInputRef"
                v-show="authenticationStep == 'RegNewPass'"
                class="registration-content__new-pass"
                v-model="authenticationData.password"
                name="password"
                v-model:error-message="authenticationErrors.newPasswordErrorMessage"
                @input-error-detected="dataLayerRegistrationError"
            />

            <RegistrationFooter
                :authentication-step="authenticationStep"
                class="registration-content__footer"
                @next-step="nextStepInternal"
                @open-login-modal="openLoginModal"
            />
        </div>
    </TransitionSlide>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import InfoText from "@/account/components/shared/info-text.vue";
import RegistrationStepsPanel from "@/account/components/registration/registration-steps-panel.vue";
import RegistrationPersonal from "@/account/components/registration/registration-personal.vue";
import PinInput from "@/account/components/inputs/pin-input.vue";
import NewPasswordInput from "@/account/components/inputs/new-password-input.vue";
import RegistrationFooter from "@/account/components/registration/registration-footer.vue";
import useAuthentication from "@/account/composables/use-authentication";
import useAuthenticationData, { authenticationErrors, authenticationData } from "@/account/composables/use-authentication-data";
import TransitionSlide from "@sazka/design-system/src/transitions/transition-slide.vue";
import { TransitionSlideDirection } from "@sazka/design-system/src/transitions/transition-slide-direction";
import useAuthenticationFlows, { authenticationStep } from "../../composables/use-authentication-flows";
import { dataLayerRegistrationError } from "@/data-layer/services/actions";

const { sendPinProcess, nextAuthenticationStep } = useAuthentication();
const { clearAuthenticationErrors } = useAuthenticationData();
const { openLoginModal } = useAuthenticationFlows();
const registrationDataComponentRef = ref(null);
const pinInputRef = ref(null);
const newPasswordInputRef = ref(null);
const transitionDirection = ref<TransitionSlideDirection>("rtl");

const nextStepInternal = () => {
    clearAuthenticationErrors();

    if (
        (authenticationStep.value == "RegPersonal" && !registrationDataComponentRef.value.checkIfIsValid()) ||
        ((authenticationStep.value == "RegVerPhone" || authenticationStep.value == "RegPinLog") && !pinInputRef.value.checkIfIsValid()) ||
        (authenticationStep.value == "RegNewPass" && !newPasswordInputRef.value.checkIfIsValid())
    ) {
        return;
    }

    nextAuthenticationStep();
};

const resendPin = async () => {
    authenticationErrors.pinErrorMessage = "";
    await sendPinProcess(true);
    pinInputRef.value.pinResended(!authenticationErrors.pinErrorMessage);
};

const modalTitle = computed(() => {
    switch (authenticationStep.value) {
        case "RegPersonal":
        case "RegVerPhone":
        case "RegPinLog":
            return "Registrace do Sazka Klubu";
        case "RegPinOk":
            return "Vaše telefonní číslo bylo úspěšně ověřeno.";
        case "RegNewPass":
            return "Už jsme skoro tam!";
        default:
            return "";
    }
});

const modalPerex = computed(() => {
    switch (authenticationStep.value) {
        case "RegPersonal":
        case "RegVerPhone":
        case "RegPinLog":
            return "Abychom s vámi mohli roztočit Kolo štěstí, potřebujeme vědět, komu vyplatíme odměnu. Nebojte, vytvoření účtu vám zabere pár minut.";
        case "RegNewPass":
            return "Abychom Vás mohli přihlásit k Vašemu Sazka Klub účtu, musíte si vytvořit heslo.";
        default:
            return "";
    }
});

const classForCurrentState = computed(() => {
    switch (authenticationStep.value) {
        case "RegPersonal":
            return "registration-content--personal";
        case "RegNewPass":
            return "registration-content--new-pass";
        case "RegVerPhone":
        case "RegPinLog":
            return "registration-content--pin";
        default:
            return "";
    }
});
</script>
